import {
  getDefaultTonic,
  getNoteByIndex,
  getNoteChromaticIndex,
  isStandardNoteName,
  type ModeLabMode,
} from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'

const VALID_MODES: readonly ModeLabMode[] = ['dorian', 'mixolydian', 'phrygian', 'lydian']
const DEFAULT_MODE: ModeLabMode = 'dorian'

const isModeLabMode = (value: string): value is ModeLabMode =>
  (VALID_MODES as readonly string[]).includes(value)

/**
 * Parse the `?mode=` URL param. Case-insensitive (lowercased before
 * validation). Silently falls back to `'dorian'` on any unknown value,
 * matching Bowie's "no error message" rule.
 *
 * Spec: docs/superpowers/specs/2026-05-20-tool-interlinking-design.md §8.2
 */
export const parseModeParam = (raw: string | null): ModeLabMode => {
  if (raw === null) {
    return DEFAULT_MODE
  }
  const normalised = raw.toLowerCase()
  return isModeLabMode(normalised) ? normalised : DEFAULT_MODE
}

/**
 * Parse the `?tonic=` URL param. Mode Lab v1's tonic <select> uses
 * `standardNoteNames` (sharps only), so:
 *
 *  - A sharp-spelled standard note (e.g., `C#`) is returned verbatim.
 *  - A flat-spelled enharmonic equivalent (e.g., `Db`) is converted to its
 *    sharp equivalent (`C#`) — pitch preservation matters more than spelling
 *    for cross-tool linking from Reharm Lab (which uses `MAJOR_TONICS` flat
 *    spellings).
 *  - Unknown or non-note values fall back to the mode's canonical default
 *    tonic (silent fallback per Bowie's "no error message" rule).
 *
 * Spec: docs/superpowers/specs/2026-05-20-tool-interlinking-design.md §8.2 / §8.3
 */
export const parseTonicParam = (raw: string | null, fallbackMode: ModeLabMode): NoteName => {
  if (raw === null) {
    return getDefaultTonic(fallbackMode)
  }
  if (isStandardNoteName(raw as NoteName)) {
    return raw as NoteName
  }
  // Try enharmonic normalisation: if `raw` resolves to a chromatic index,
  // return the canonical sharp-spelled note at that index. Otherwise fall back.
  const chromaticIndex = getNoteChromaticIndex(raw as NoteName)
  if (chromaticIndex < 0) {
    return getDefaultTonic(fallbackMode)
  }
  return getNoteByIndex(chromaticIndex)
}
